import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  type TextFieldProps,
  Typography,
} from "@mui/material";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { Account } from "Components/Shared/Account/Account";
import { ArrowRightIcon } from "Components/Shared/Icons";
import {
  BankAccountStatus,
  type BankAccountListItem,
} from "Infrastructure/Api/Api";
import { appUrl } from "Utils/UrlUtils";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { Resources, useResource } from "Translations/Resources";
import type { ReactNode } from "react";

const COMPONENT_RESOURCES = Resources.Components.Shared.BankingAccountSelector;

type Props<
  FormType extends FieldValues,
  TransformedFieldValues = FormType,
> = TextFieldProps & {
  name: FieldPath<FormType>;
  label?: string;
  control: Control<FormType, unknown, TransformedFieldValues>;
  disabled?: boolean;
  bankAccounts: BankAccountListItem[];
  additionalContent?: ReactNode;
};

export const BankingAccountSelector = <T extends object, V extends object>({
  control,
  name,
  label,
  bankAccounts,
  additionalContent,
}: Props<T, V>) => {
  const { t } = useResource();

  if (bankAccounts.length === 0) {
    return (
      <Stack gap={1}>
        <Typography fontWeight={400}>{label}</Typography>
        <Typography display="flex" gap={1} flexWrap="wrap" color="error">
          {t(COMPONENT_RESOURCES.NoBankAccounts)}
          <UnstyledLink to={appUrl("bank-address-list")}>
            <Box component="span" display="flex" alignItems="center" gap={1}>
              {t(COMPONENT_RESOURCES.ConnectBankAccount)}
              <ArrowRightIcon height="0.7rem" width="0.7rem" />
            </Box>
          </UnstyledLink>
        </Typography>
      </Stack>
    );
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, ...field }, fieldState: { error } }) => (
        <FormControl
          component="fieldset"
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            flexGrow: 1,
            height: ["156px", "auto"],
            maxHeight: ["auto", "500px"],
          }}
          error={!!error}
        >
          {label && (
            <Box mb={1}>
              <Typography
                component="label"
                htmlFor={field.name}
                fontWeight={400}
              >
                {label}
              </Typography>
            </Box>
          )}
          <RadioGroup
            {...field}
            value={field.value ?? ""}
            onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
            sx={{
              flexWrap: "nowrap",
              overflowY: "auto",
              minHeight: 0,
              height: "100%",
              px: 0.5,
            }}
          >
            {bankAccounts.map((account, index, items) => {
              const isDisabled =
                account.status !== BankAccountStatus.Active &&
                account.status !== BankAccountStatus.Restricted;

              return (
                <FormControlLabel
                  key={account.bankAccountID}
                  value={account.bankAccountID}
                  control={
                    <Radio
                      sx={{ display: "none" }}
                      checked={field.value === account.bankAccountID}
                      disabled={isDisabled}
                    />
                  }
                  sx={{
                    width: "100%",
                    mb: index < items.length - 1 ? 2 : 0,
                    ml: 0,
                    "& .MuiFormControlLabel-label": {
                      width: "100%",
                      margin: 0,
                    },
                    mx: 0,
                  }}
                  label={
                    <AccountOption
                      account={account}
                      isSelected={field.value === account.bankAccountID}
                      isDisabled={isDisabled}
                    />
                  }
                />
              );
            })}
            {additionalContent}
          </RadioGroup>
          {error?.message && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

type AccountOptionProps = {
  isSelected: boolean;
  account: BankAccountListItem;
  isDisabled: boolean;
};

const AccountOption = ({
  isSelected,
  account,
  isDisabled,
}: AccountOptionProps) => (
  <Box
    display="flex"
    justifyContent="space-between"
    boxShadow="none"
    borderRadius="10px"
    py={1}
    px={isSelected ? "8px" : "9px"}
    my={isSelected ? 0 : "1px"}
    sx={({ palette, colors }) => ({
      "&:hover": {
        backgroundColor: isDisabled ? `${colors.gray}80` : palette.grey[100],
      },
      backgroundColor: isDisabled ? `${colors.gray}80` : "transparent",
      border: isSelected ? "2px solid" : "1px solid",
      borderColor: isSelected ? palette.primary.main : palette.grey[300],
    })}
  >
    <Account {...account} verificationStatusVisibility="intermediateOnly" />
  </Box>
);
